import { useState, useRef, useEffect, forwardRef } from 'react';
import SignaturePad from 'react-signature-pad-wrapper';
import Portal from "../../lib/customHooks/usePortal";
import { useResize } from "../../lib/customHooks/useResize";
import { useTempStorage, clearTempStorage, dataStore } from "../../lib/customHooks/useTempStorage";
import { ClientInput } from "../inputs/ClientInput";
import { SinglePhotoInfo, VisualDropArea, defaultSizeLimit, defaultTypes, fileTooLargeError, fileChangeTooLargeError, unsupportedFileError, genericError } from "../inputs/ClientPhotoUpload";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-light-svg-icons';
const TypeTab = forwardRef(({
  typeTabState,
  setTypeTabState
}, typeCanvasRef) => {
  const [name, setName] = useState('');
  const {
    width
  } = useResize();
  const fillCanvasWithText = (context, width, height, text, fontSize) => {
    context.clearRect(0, 0, width, height);
    context.fillStyle = 'rgb(255,255,255)';
    context.fillRect(0, 0, width, height);
    context.fillStyle = 'black';
    context.font = `${fontSize}px Pacifico`;
    context.textBaseline = 'middle';
    context.fillText(text, 30, height / 2);
  };
  const generateSignaturePreview = () => {
    const defaultCanvasFontSize = 48;
    const trimmedName = name.trim();
    const canvas = typeCanvasRef.current;
    const {
      width,
      height
    } = canvas.parentElement.getBoundingClientRect();
    const context = canvas.getContext('2d');
    fillCanvasWithText(context, width, height, trimmedName, defaultCanvasFontSize);

    // resize text if it exceeds the width of the canvas
    const maxWidth = width - 60;
    let currentWidth = context.measureText(trimmedName).width;
    if (currentWidth > maxWidth) {
      let newFontSize = defaultCanvasFontSize;
      const decrement = 1;
      while (currentWidth > maxWidth) {
        newFontSize -= decrement;
        fillCanvasWithText(context, width, height, trimmedName, newFontSize);
        currentWidth = context.measureText(trimmedName).width;
      }
    }
  };

  // TODO: debounce, though this is complicated because of rerenders/refs/callbacks
  useEffect(() => {
    // sets true canvas size, necessary on the initial setup & on each resize
    const canvas = typeCanvasRef.current;
    const {
      width
    } = canvas.parentElement.getBoundingClientRect();
    canvas.width = width;
    if (name) {
      generateSignaturePreview();
      if (!typeTabState?.hasSignature) {
        setTypeTabState({
          ...typeTabState,
          hasSignature: true
        });
      }
    }
  }, [width]);
  useEffect(() => {
    // render signature preview as user types into text input
    generateSignaturePreview();
    setTypeTabState({
      ...typeTabState,
      tempName: name?.trim(),
      hasSignature: name?.trim().length ? true : false
    });
  }, [name]);
  useEffect(() => {
    if (typeTabState?.tempName) {
      // restore temp name
      setName(typeTabState.tempName);
    } else if (typeTabState?.savedName) {
      // restore previously captured name
      const {
        savedName
      } = typeTabState;
      setTypeTabState({
        ...typeTabState,
        tempName: savedName
      });
      setName(savedName);
    }
  }, []);
  return <div className="tab--container type-tab--container">
        <div className="type-tab--input">
          <ClientInput placeholder={' '} value={name} update={value => setName(value)} label="Please Type Your Full Name" maxLength="61" />
        </div>
        <p className="type-tab--preview">Preview</p>
        <div className="type-tab--canvas-container">
          <canvas ref={typeCanvasRef} className="type-tab--canvas" height="120" />
        </div>
      </div>;
});
const DrawTab = forwardRef(({
  drawTabState = {},
  setDrawTabState,
  update
}, currentSignaturePad) => {
  const {
    width
  } = useResize();
  const {
    tempLines,
    hasSignature,
    savedLines
  } = drawTabState;
  const options = {
    clearButton: true,
    redrawOnResize: true,
    dotSize: 1,
    backgroundColor: 'rgb(255,255,255)',
    // must maintain a white background
    width: 422,
    // will be doubled on 2x devices, and thus needs to be scaled down in useEffect
    height: 200,
    onEnd: () => setDrawTabState({
      ...drawTabState,
      tempLines: currentSignaturePad.current.toData(),
      hasSignature: true
    })
  };
  const clearPad = () => {
    if (!currentSignaturePad.current.isEmpty()) {
      currentSignaturePad.current.clear();
      update(null);
      setDrawTabState({
        hasSignature: false,
        savedLines: false,
        tempLines: false
      });
    }
  };

  // TODO: debounce, though this is complicated because of rerenders/refs/callbacks
  useEffect(() => {
    // sets true canvas size, necessary on the initial setup & on each resize
    const {
      canvas
    } = currentSignaturePad.current.signaturePad;
    const {
      width,
      height
    } = canvas.parentElement.getBoundingClientRect();
    const context = canvas.getContext('2d');
    canvas.height = height;
    canvas.width = width;
    context.fillStyle = 'rgb(255,255,255)';
    context.fillRect(0, 0, canvas.width, canvas.height);
    if (tempLines) {
      currentSignaturePad.current.clear();
      context.fillStyle = 'rgb(255,255,255)';
      context.fillRect(0, 0, canvas.width, canvas.height);
      currentSignaturePad.current.fromData(tempLines);
      if (!hasSignature) {
        setDrawTabState({
          ...drawTabState,
          hasSignature: true
        });
      }
    }
  }, [width]);
  useEffect(() => {
    // restore a previously captured signature
    if (savedLines) {
      const {
        canvas
      } = currentSignaturePad.current.signaturePad;
      const {
        width,
        height
      } = canvas.parentElement.getBoundingClientRect();
      const context = canvas.getContext('2d');
      const savedLineData = Array.from(drawTabState.savedLines);
      setDrawTabState({
        ...drawTabState,
        tempLines: savedLineData
      });
      currentSignaturePad.current.clear();
      context.fillStyle = 'rgb(255,255,255)';
      context.fillRect(0, 0, width, height);
      currentSignaturePad.current.fromData(savedLineData);
    }
  }, []);
  return <div className="tab--container draw-tab--container">
        <div className="draw-tab--intro">
          <p>Draw in the field below to create your signature</p>
        </div>
        <div className="signature-pad--notice">
          Signature works best in landscape. Try turning your phone!
        </div>
        <div className="draw-tab--canvas-container">
          <SignaturePad redrawOnResize options={options} ref={currentSignaturePad} />
        </div>
        <button className="btn--link signature-pad--clear" onClick={clearPad}>
          Clear Signature
        </button>
      </div>;
});
const UploadTab = ({
  uploadTabState,
  setUploadTabState,
  update
}) => {
  const fileInputRef = useRef();
  const [errorMessage, setErrorMessage] = useState('');
  const acceptedFileTypes = defaultTypes.split(',');

  // checking the files added for various errors
  const onFileChange = event => {
    const file = event.target.files[0];
    if (file && file.size > defaultSizeLimit) {
      event.preventDefault();
      fileInputRef.current.value = '';
      if (uploadTabState?.tempImage) {
        setErrorMessage(fileChangeTooLargeError);
      } else {
        setErrorMessage(fileTooLargeError);
      }
    } else if (file && acceptedFileTypes.indexOf(file.type) === -1) {
      event.preventDefault();
      fileInputRef.current.value = '';
      setErrorMessage(unsupportedFileError);
    } else if (file) {
      const {
        url,
        size,
        type,
        name
      } = file;
      setUploadTabState({
        ...uploadTabState,
        tempImage: {
          url: url || URL.createObjectURL(file),
          size,
          type,
          name
        },
        hasSignature: true
      });
      setErrorMessage('');
    }
  };
  const removeFile = () => {
    fileInputRef.current.value = '';
    update(null);
    setUploadTabState({
      hasSignature: false,
      savedImage: false,
      tempImage: false
    });
  };

  // we want to prevent accidental drops outside the file inputs
  // from replacing the entire page with the dropped image
  useEffect(() => {
    const maybeCancelDropEvent = e => {
      const {
        dataTransfer
      } = e;
      if (e.target !== fileInputRef.current) {
        e.preventDefault();
        dataTransfer.effectAllowed = 'none';
        dataTransfer.dropEffect = 'none';
        return false;
      } else {
        dataTransfer.effectAllowed = 'all';
        dataTransfer.dropEffect = 'move';
      }
    };

    // add drop event to cancel file drops outside the input
    document.addEventListener('drop', maybeCancelDropEvent, false);
    document.addEventListener('dragover', maybeCancelDropEvent, false);
    return () => {
      // remove event canceling drop events
      document.removeEventListener('drop', maybeCancelDropEvent);
      document.removeEventListener('dragover', maybeCancelDropEvent);
    };
  }, []);
  useEffect(() => {
    // restore previously captured signature
    if (uploadTabState?.savedImage) {
      setUploadTabState({
        ...uploadTabState,
        tempImage: uploadTabState.savedImage
      });
    }
  }, []);
  return <div className="tab--container upload-tab--container">
      <div className="photo-upload--area">
        {!uploadTabState?.tempImage && <VisualDropArea isMultiple={false} inputRef={fileInputRef} errorMessage={errorMessage} />}
        <input ref={fileInputRef} type="file" onChange={onFileChange} onError={e => setErrorMessage(e.message)} accept={acceptedFileTypes} className="photo-upload--input" />
      </div>
      {uploadTabState?.tempImage && <SinglePhotoInfo file={uploadTabState.tempImage} removeFile={removeFile} fileInput={fileInputRef} sizeLimit={defaultSizeLimit} reportError={isError => {
      if (isError) {
        removeFile(removeFile);
        setErrorMessage(genericError);
      }
    }} />}
      {!!errorMessage && <div className="portal-modal--error">
          <i className="foundry-icon icon--exclamation-triangle icon--red"></i>
          {errorMessage}
        </div>}
    </div>;
};
const SignatureTabs = ({
  setModalOpenState,
  lastFocused,
  update,
  guid,
  savedImageUrl
}) => {
  const typeTabGuid = `typeTab-${guid}`;
  const drawTabGuid = `drawTab-${guid}`;
  const uploadTabGuid = `uploadTab-${guid}`;
  const tabs = [{
    id: typeTabGuid,
    label: 'Type'
  }, {
    id: drawTabGuid,
    label: 'Draw'
  }, {
    id: uploadTabGuid,
    label: 'Upload'
  }];
  const [typeTabState, setTypeTabState] = useTempStorage(typeTabGuid);
  const [drawTabState, setDrawTabState] = useTempStorage(drawTabGuid);
  const [uploadTabState, setUploadTabState] = useTempStorage(uploadTabGuid);
  const [activeTab, setActiveTab] = useTempStorage(`activeTab-${guid}`);
  const typeCanvasRef = useRef();
  const drawCanvasRef = useRef();
  const captureButtonEnabled = dataStore.get(activeTab?.id)?.hasSignature;
  const saveCanvasImage = canvasRef => {
    const dataUrl = canvasRef.current.toDataURL('image/jpeg', 1.0);
    return fetch(dataUrl).then(res => res.blob()) // quick and dirty blobbing
    .then(blob => URL.createObjectURL(blob)) // local use only
    .then(localUrl => {
      if (savedImageUrl) {
        // clean up old image
        URL.revokeObjectURL(savedImageUrl);
      }
      update(localUrl); // stores a local blob url in the store instead of entire file
    });
  };
  const captureSignature = () => {
    if (activeTab?.id === typeTabGuid) {
      const {
        tempName: name
      } = typeTabState;
      saveCanvasImage(typeCanvasRef).then(() => {
        setTypeTabState({
          ...typeTabState,
          savedName: name,
          tempName: false
        });
        clearTempStorage(drawTabGuid);
        clearTempStorage(uploadTabGuid);
        setModalOpenState(false);
      });
    }
    if (activeTab?.id === drawTabGuid) {
      saveCanvasImage(drawCanvasRef).then(() => {
        const lineData = drawCanvasRef.current.toData();
        setDrawTabState({
          ...drawTabState,
          savedLines: lineData,
          tempLines: false
        });
        clearTempStorage(typeTabGuid);
        clearTempStorage(uploadTabGuid);
        setModalOpenState(false);
      });
    }
    if (activeTab?.id === uploadTabGuid) {
      if (savedImageUrl) {
        // clean up old image
        URL.revokeObjectURL(savedImageUrl);
      }
      const {
        tempImage: uploadedImage
      } = uploadTabState;
      update(uploadedImage.url); // stores a local blob url in the store instead of entire file
      setUploadTabState({
        ...uploadTabState,
        savedImage: uploadedImage,
        tempImage: false
      });
      clearTempStorage(typeTabGuid);
      clearTempStorage(drawTabGuid);
      setModalOpenState(false);
    }
  };
  const cancelCapture = () => {
    if (!savedImageUrl) {
      clearTempStorage(typeTabGuid);
      clearTempStorage(drawTabGuid);
      clearTempStorage(uploadTabGuid);
    } else {
      setTypeTabState({
        ...typeTabState,
        tempName: false
      });
      setDrawTabState({
        ...drawTabState,
        tempLines: false
      });
      setUploadTabState({
        ...uploadTabState,
        tempImage: false
      });
    }
    setModalOpenState(false);
    if (lastFocused) {
      lastFocused.focus();
    }
  };
  useEffect(() => {
    if (!activeTab) {
      setActiveTab(tabs[0]);
    }
  }, []);
  useEffect(() => {
    const close = e => {
      if (e.key === 'Escape') {
        cancelCapture();
      }
    };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, []);
  return <div className="portal-modal--ui-container">
      <button className="preview--close" onClick={cancelCapture}>
        <FontAwesomeIcon className="web-form-preview-close--btn" icon={faTimes} role="img" title="Close Signature Modal" />
      </button>
      <div className="portal-modal--intro">
        <h2 className="portal-modal--heading">Insert eSignature</h2>
        <p>Use one of the following methods to create your signature</p>
      </div>
      <div className="signature-tabs--container">
        <nav className="signature-tabs control-group">
          {tabs.map(tab => <div key={tab.id} onClick={() => setActiveTab(tab)} className={`signature-tab ${activeTab?.id === tab.id ? 'active' : ''}`}>
              {tab.label}
            </div>)}
        </nav>
        <div className="signature-panel--active-tab">
          {activeTab?.id === typeTabGuid && <TypeTab typeTabState={typeTabState} setTypeTabState={setTypeTabState} ref={typeCanvasRef} />}
          {activeTab?.id === drawTabGuid && <DrawTab drawTabState={drawTabState} setDrawTabState={setDrawTabState} ref={drawCanvasRef} update={update} />}
          {activeTab?.id === uploadTabGuid && <UploadTab uploadTabState={uploadTabState} setUploadTabState={setUploadTabState} update={update} />}
        </div>
      </div>
      <div className="portal-modal--controls">
        <hr />
        <button className="foundry--button btn--link" onClick={cancelCapture}>
          Cancel
        </button>
        <button className={`foundry--button ${captureButtonEnabled ? `button--active` : `button--disabled`}`} disabled={!captureButtonEnabled} onClick={captureSignature}>
          Capture Signature
        </button>
      </div>
    </div>;
};
export const SignaturePreview = ({
  guid,
  value,
  label,
  required,
  errors,
  disabled = false,
  update
}) => {
  const [modalOpen, setModalOpenState] = useState(false);
  const showFinalImagePreview = false;
  const [lastFocused, setLastFocused] = useState(() => document.activeElement);
  const openModal = () => {
    setModalOpenState(true);
    if (lastFocused) {
      // allow us to return to previous tab focus, despite blur
      setLastFocused(document.activeElement);
    }
    if (document.activeElement) {
      document.activeElement.blur();
    }
  };
  const onKeyPress = e => {
    e.preventDefault();
    openModal();
  };
  return <div className="signature-pad--field" data-saved-image={value}>
      <ClientInput onClick={openModal} onKeyPress={onKeyPress} additionalClass={`${value && !errors ? 'completed' : 'incomplete'}`} placeholder={' '} readOnly inputMode="none" value={value ? `Signature Captured` : `Capture Signature`} label={required ? `${label}*` : label} error={!!errors.length} errorText={errors} disabled={disabled} style={{
      caretColor: 'transparent'
    }} />
      {showFinalImagePreview && <img style={{
      width: '100%'
    }} src={value} />}
      {modalOpen && <Portal className="portal-modal">
          <div className="portal-modal--background" />
          <div className="portal-modal--modal-content">
            <SignatureTabs setModalOpenState={setModalOpenState} lastFocused={lastFocused} update={update} guid={guid} savedImageUrl={value} />
          </div>
        </Portal>}
    </div>;
};